import React, { useCallback } from 'react';

import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(1.5),
    paddingTop: theme.spacing(0),
  },
  paperContainer: {
    width: '100%',
    padding: theme.spacing(1),
    position: 'relative',
    zIndex: 1,
  },
  formInput: {
    padding: `${theme.spacing(0)} !important`,
    '& .MuiInputBase-input': {
      padding: `${theme.spacing(0.25)} !important`,
      ...theme.typography.paragraphRegular,
      overflow: 'auto !important' as 'auto',
      resize: 'none',
    },
    '& .MuiInputBase-multiline': {
      paddingY: theme.spacing(0.25),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

interface ChatControlsProps {
  input: string;
  isStreaming: boolean;
  isReady: boolean;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  recording?: boolean;
  startRecording?: () => void;
  stopRecording?: () => void;
  isLoading: boolean;
  sendMessageText: (text: string, trackSend?: boolean) => void;
}

type ActionButtonProps = Omit<ChatControlsProps, 'setInput' | 'sendMessageText'>;
const ASSIST_DISCLAIMER_PLACEHOLDER_URL =
  'https://support.simplifi.quicken.com/en/articles/13553075-quicken-assist-disclosure-and-consent';

const ActionButton = ({
  input,
  recording,
  isLoading,
  isReady,
  startRecording = undefined,
  stopRecording = undefined,
}: ActionButtonProps) => {
  if (Boolean(startRecording) && Boolean(stopRecording && input.length === 0)) {
    if (recording) {
      return (
        <IconButton variant='contained' size='small' onClick={() => stopRecording && stopRecording()}>
          <MicOffRoundedIcon />
        </IconButton>
      );
    } else {
      return (
        <IconButton
          onClick={() => {
            startRecording && startRecording();
          }}
          variant='contained'
          size='small'
        >
          <GraphicEqRoundedIcon />
        </IconButton>
      );
    }
  }

  return (
    <IconButton color='primary' variant='contained' size='small' disabled={isLoading || !isReady} type='submit'>
      <ArrowUpwardRoundedIcon />
    </IconButton>
  );
};

const ChatControls = ({
  input,
  setInput,
  recording,
  startRecording,
  stopRecording,
  isStreaming,
  isLoading,
  isReady,
  sendMessageText,
}: ChatControlsProps) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [placeholderRevealed, setPlaceholderRevealed] = React.useState(false);
  const maxRows = 6;

  React.useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setPlaceholderRevealed(true);
      return;
    }
    const id = window.requestAnimationFrame(() => {
      setPlaceholderRevealed(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  const syncTextareaHeight = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    const textarea = inputRef.current;
    if (!textarea || !(textarea instanceof HTMLTextAreaElement)) return;

    textarea.style.height = 'auto';
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight || '0');
    const paddingTop = parseFloat(computedStyle.paddingTop || '0');
    const paddingBottom = parseFloat(computedStyle.paddingBottom || '0');
    const borderTop = parseFloat(computedStyle.borderTopWidth || '0');
    const borderBottom = parseFloat(computedStyle.borderBottomWidth || '0');
    const maxHeight =
      lineHeight > 0 ? lineHeight * maxRows + paddingTop + paddingBottom + borderTop + borderBottom : undefined;
    const nextHeight = textarea.scrollHeight;
    const clampedHeight = maxHeight ? Math.min(nextHeight, maxHeight) : nextHeight;

    textarea.style.height = `${clampedHeight}px`;
    textarea.style.overflowY = nextHeight > clampedHeight ? 'auto' : 'hidden';
  }, [maxRows]);

  React.useEffect(() => {
    syncTextareaHeight();
  }, [input, syncTextareaHeight]);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      sendMessageText(input, true);
    },
    [input, sendMessageText],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessageText(input, true);
      }
    },
    [input, sendMessageText],
  );

  return (
    <Box className={classes.container}>
      <Paper elevation={16} className={classes.paperContainer}>
        <form onSubmit={handleFormSubmit}>
          <OutlinedInput
            autoFocus
            className={classes.formInput}
            fullWidth
            size='small'
            multiline
            rows={1}
            placeholder='Ask anything...'
            value={input}
            inputComponent='textarea'
            inputRef={inputRef}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleInputKeyDown}
            sx={{
              '& .MuiInputBase-input::placeholder': {
                opacity: placeholderRevealed ? 1 : 0,
                transition: 'opacity 0.45s ease-out',
                color: theme.palette.text.tertiary,
              },
            }}
          />
          <Box className={classes.footer}>
            <Typography variant='captionRegular' color='text.disabled'>
              <Link
                href={ASSIST_DISCLAIMER_PLACEHOLDER_URL}
                target='_blank'
                rel='noopener noreferrer'
                color='inherit'
                underline='none'
                sx={{
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline dotted',
                  },
                }}
              >
                Quicken Assist (Beta)
              </Link>{' '}
              can make mistakes. Verify results.
            </Typography>
            <ActionButton
              input={input}
              isStreaming={isStreaming}
              isLoading={isLoading}
              isReady={isReady}
              recording={recording}
              startRecording={startRecording}
              stopRecording={stopRecording}
            />
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ChatControls;
